import { useState } from 'react'
import type { Elevmaterial, ElevmaterialDistribution } from '../types'
import { Figurer } from './Figurer'
import SvarsutrymmeVy from './SvarsutrymmeVy'
import Tavelvy from './Tavelvy'
import UtskriftPortal, { useUtskrift } from './UtskriftPortal'

/** Distributionen i klartext — det läraren behöver veta för att skriva ut rätt
 *  antal, eller för att inte skriva ut alls. */
const DISTRIBUTION_ETIKETT: Record<ElevmaterialDistribution, string> = {
  per_elev: 'En per elev',
  per_par: 'En per par',
  per_grupp: 'En per grupp',
  per_station: 'En per station',
  helskarm: 'Visas på tavlan',
  hoglasning: 'Läses högt',
  larare: 'Lärarens eget',
}

/** Material som projiceras för hela klassen skrivs inte ut — det visas. */
const projiceras = (distribution: ElevmaterialDistribution) => distribution === 'helskarm'

/** Facit får bara följa med dit läraren är ensam mottagare: aldrig upp på tavlan,
 *  och aldrig med på det som delas ut. På skärmen ligger det bakom en utfällning;
 *  i utskriften kommer det med enbart när materialet i sig är lärarens eget. */
const facitFarVisas = (distribution: ElevmaterialDistribution) => !projiceras(distribution)
const facitFarSkrivasUt = (distribution: ElevmaterialDistribution) => distribution === 'larare'

/** Materialets innehåll — samma komponent på skärmen, på tavlan och i utskriften,
 *  så att förhandsvisningen är trogen det som kommer ut ur skrivaren. */
function ElevmaterialInnehall({ material, medFacit = false }: { material: Elevmaterial; medFacit?: boolean }) {
  const { typ, avsnitt, fragor } = material

  return (
    <>
      {material.instruktion && <p className="elevmaterial-instruktion">{material.instruktion}</p>}

      {typ === 'text' && material.text && <p className="elevmaterial-text">{material.text}</p>}

      {/* Figuren är tryckt på materialet — utan visningsmärkning, som är lärarens. */}
      <Figurer illustrationer={material.illustrationer} />

      {avsnitt.length > 0 && (
        <div className={typ === 'kort' ? 'elevmaterial-kortlek' : 'elevmaterial-avsnitt'}>
          {avsnitt.map((del, i) => (
            <div key={i} className={typ === 'kort' ? 'elevmaterial-kort' : 'elevmaterial-del'}>
              {del.rubrik && <p className="elevmaterial-delrubrik">{del.rubrik}</p>}
              {del.text && <p className="elevmaterial-text">{del.text}</p>}
              {del.punkter.length > 0 && (
                <ul className="elevmaterial-punkter">
                  {del.punkter.map((punkt, j) => (
                    <li key={j}>{punkt}</li>
                  ))}
                </ul>
              )}
              {del.ifyllnadsrader !== null && <SvarsutrymmeVy svarsutrymme={{ typ: 'rader', antal: del.ifyllnadsrader }} />}
            </div>
          ))}
        </div>
      )}

      {fragor.length > 0 && (
        <ol className="elevmaterial-fragor">
          {fragor.map((fraga) => (
            <li key={fraga.nummer} value={fraga.nummer}>
              <p className="elevmaterial-fragetext">{fraga.text}</p>
              {fraga.alternativ.length > 0 && (
                <ul className="elevmaterial-alternativ">
                  {fraga.alternativ.map((alt, i) => (
                    <li key={i}>{alt}</li>
                  ))}
                </ul>
              )}
              {medFacit && (
                <p className="elevmaterial-radfacit">
                  <strong>Facit: </strong>
                  {fraga.ratt_svar_index !== null ? (fraga.alternativ[fraga.ratt_svar_index] ?? '—') : (fraga.facit ?? '—')}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </>
  )
}

/** Ett färdigt elevmaterial i lärarvyn: en kompakt rad med titel, hur det ska
 *  delas ut och knappen som faktiskt behövs — skriv ut, eller visa på tavlan när
 *  materialet är avsett att projiceras. Innehållet ligger bakom en förhandsvisning
 *  så att blockkortet inte växer tillbaka till en vägg av text. Utskriften går
 *  genom UtskriftPortal och får därmed samma rena format som arbetsblad och prov. */
export default function ElevmaterialVy({ material }: { material: Elevmaterial }) {
  const utskrift = useUtskrift()
  const [visarTavla, setVisarTavla] = useState(false)

  const { distribution, typ, fragor } = material
  const harFacit = fragor.some((f) => f.facit || f.ratt_svar_index !== null)
  const visaFacitPaSkarm = harFacit && facitFarVisas(distribution)
  // Blanketter fylls i av eleven och behöver därför en namnrad vid utskrift.
  const medNamnrad = typ === 'blankett'

  return (
    // Typmodifieraren har eget prefix: utan det krockar typ "kort" och "text"
    // med innehållsklasserna .elevmaterial-kort/.elevmaterial-text nedan, så att
    // hela kortet ärver stilar som är avsedda för dess delar.
    <div className={`elevmaterial elevmaterial-typ-${typ}`}>
      <div className="elevmaterial-huvud">
        <h6>{material.titel}</h6>
        <div className="elevmaterial-atgarder">
          <span className={`elevmaterial-distribution elevmaterial-distribution-${distribution}`}>
            {DISTRIBUTION_ETIKETT[distribution] ?? distribution}
          </span>
          {projiceras(distribution) ? (
            <button type="button" className="knapp knapp-sekundar knapp-liten no-print" onClick={() => setVisarTavla(true)}>
              🖥 Visa på tavlan
            </button>
          ) : (
            <button type="button" className="knapp knapp-sekundar knapp-liten no-print" onClick={utskrift.skrivUt}>
              🖨 Skriv ut
            </button>
          )}
        </div>
      </div>

      <details className="elevmaterial-forhandsvisning">
        <summary>Förhandsgranska</summary>
        <div className="elevmaterial-innehall">
          <ElevmaterialInnehall material={material} />
        </div>
        {visaFacitPaSkarm && (
          <details className="elevmaterial-facit">
            <summary>Facit — visas inte för eleverna</summary>
            <ol>
              {fragor.map((fraga) => (
                <li key={fraga.nummer} value={fraga.nummer}>
                  {fraga.ratt_svar_index !== null ? (fraga.alternativ[fraga.ratt_svar_index] ?? '—') : (fraga.facit ?? '—')}
                </li>
              ))}
            </ol>
          </details>
        )}
      </details>

      {visarTavla && (
        <Tavelvy rubrik={material.titel} onStang={() => setVisarTavla(false)}>
          <div className="tavelvy-elevmaterial">
            <ElevmaterialInnehall material={material} />
          </div>
        </Tavelvy>
      )}

      {utskrift.aktiv && (
        <UtskriftPortal onKlar={utskrift.avsluta}>
          <div className="elevmaterial-utskrift">
            <h1 className="arbetsblad-rubrik">{material.titel}</h1>
            {medNamnrad && <p className="arbetsblad-namnrad">Namn: _______________________ Klass: _________ Datum: _________</p>}
            <ElevmaterialInnehall material={material} medFacit={facitFarSkrivasUt(distribution)} />
          </div>
        </UtskriftPortal>
      )}
    </div>
  )
}
